import type { RequireAtLeastOne } from '@/utils/tsutils';

import {
  APOLLO_CLIENTS,
  initializeApollo,
  initializeSecureApolloClient,
} from '@/graphql/client';
import type {
  OrderAppError,
  OrderAppPaymentMethodMetadataUpdateMutation,
  OrderAppPaymentMethodMetadataUpdateMutationVariables,
} from '@/graphql/orderApp/api';
import { PaymentMethodMetadataUpdateDocument } from '@/graphql/orderApp/api';
import type {
  CheckoutTransactionsQuery,
  CheckoutTransactionsQueryVariables,
  OrderTransactionsQuery,
  OrderTransactionsQueryVariables,
} from '@/graphql/saleor/api';
import {
  CheckoutTransactionsDocument,
  OrderTransactionsDocument,
} from '@/graphql/saleor/api';
import { captureMessage } from '@/lib/errors';
import { paths } from '@/lib/paths';

import { getPaymentMethodName } from '../order';
import { isTokenizablePaymentMethod } from './helpers';
import type { PaymentMethod, PaymentRecord, PaymentType } from './types';

export const restorePaymentMethods = async (
  opts: RequireAtLeastOne<{ checkoutId: string; orderId: string }>,
): Promise<PaymentMethod[]> => {
  const response = await fetch(paths.api.paymentMethods.asPath(), {
    method: 'POST',
    body: JSON.stringify(opts),
  });

  if (!response.ok) {
    return [];
  }

  return await response.json();
};

export const retrievePaymentData = async ({
  checkoutId,
  orderId,
}: RequireAtLeastOne<{ checkoutId: string; orderId: string }>) => {
  const client = initializeSecureApolloClient();
  let data:
    | CheckoutTransactionsQuery['checkout']
    | OrderTransactionsQuery['order'];

  if (checkoutId) {
    const response = await client.query<
      CheckoutTransactionsQuery,
      CheckoutTransactionsQueryVariables
    >({
      query: CheckoutTransactionsDocument,
      variables: { id: checkoutId },
    });
    data = response.data.checkout;
  } else {
    const response = await client.query<
      OrderTransactionsQuery,
      OrderTransactionsQueryVariables
    >({
      query: OrderTransactionsDocument,
      variables: { id: orderId! },
    });
    data = response.data.order;
  }

  if (!data) {
    return [];
  }

  const authorizedTransactions =
    data?.transactions?.filter(
      ({ authorizedAmount, chargePendingAmount, chargedAmount, events }) =>
        events.some(
          ({ type }) =>
            (type === 'AUTHORIZATION_SUCCESS' || type === 'CHARGE_SUCCESS') &&
            (authorizedAmount?.amount ?? 0) +
              (chargePendingAmount?.amount ?? 0) +
              (chargedAmount?.amount ?? 0) >
              0,
        ),
    ) ?? [];
  const source = data?.orderSource;
  const paymentData = authorizedTransactions
    .map((transaction) => {
      const {
        id,
        paymentData,
        paymentMethod: paymentType,
        tokenData,
      } = transaction;
      try {
        const paymentMethod: PaymentRecord = {
          type: paymentType as PaymentType,
          gateway: '',
          name: getPaymentMethodName(transaction, source) ?? '',
          amount:
            (transaction.authorizedAmount?.amount ?? 0) +
            (transaction.chargePendingAmount?.amount ?? 0) +
            (transaction.chargedAmount?.amount ?? 0),
          id: id,
        };
        if (paymentData) {
          const dataPayload = JSON.parse(paymentData).payload;
          paymentMethod.gateway = dataPayload.issuing_app;
          if (isTokenizablePaymentMethod(paymentMethod.type)) {
            paymentMethod.extraInfo = {
              id: '',
              expiryDate: dataPayload.card_expiry,
              last4: dataPayload.card_last_4,
              holderName: dataPayload.card_holder,
            };
          }
        }
        return paymentMethod;
      } catch {
        captureMessage('Could not parse transaction `paymentData`', {
          extra: {
            paymentData,
            tokenData,
            ...data,
          },
        });
      }
    })
    .filter(Boolean);

  return paymentData;
};

export const storePaymentData = async (
  variables: OrderAppPaymentMethodMetadataUpdateMutationVariables,
): Promise<OrderAppError[]> => {
  const client = initializeApollo();

  const { data } = await client.mutate<
    OrderAppPaymentMethodMetadataUpdateMutation,
    OrderAppPaymentMethodMetadataUpdateMutationVariables
  >({
    mutation: PaymentMethodMetadataUpdateDocument,
    variables,
    context: {
      client: APOLLO_CLIENTS.orderApp,
    },
  });

  return data?.updateMetadata?.errors ?? [];
};
